// ** MUI Imports
import { Theme } from '@mui/material/styles';


declare module '@mui/material' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
  interface ToggleButtonPropsSizeOverrides {
    fixed: true;
  }
}

const Button = (theme: Theme) => {
  return {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
        variant: 'contained',
        color: 'primary',
      },
      styleOverrides: {
        root: {
          fontSize: 13,
          lineHeight: '16px',
          textTransform: 'none',
          borderRadius: 4,
          padding: '4px 16px',
          minHeight: 32,
          gap: 4,
          outline: 'none',
          '& svg': { height: 16 },
        },
        containedPrimary: {
          backgroundColor: 'var(--c-background-dark)',
          color: 'var(--c-text-contrast)',
          '&:hover, &:active, &:focus-within': {
            backgroundColor: 'var(--c-hover-on-dark)',
          },
          '&:disabled': {
            backgroundColor: 'var(--c-background-dark)',
            color: 'var(--c-text-contrast-disabled)',
          },
          '& svg': {
            color: 'inherit',
          },
          '& .MuiLoadingButton-loadingIndicator': {
            color: 'var(--c-text-contrast-disabled)',
          },
        },
        containedSecondary: {
          background: 'var(--c-background-light)',
          color: 'var(--c-secondary)',
          border: '1px solid #E8E8E8',
          '&:hover, &:active, &:focus-within': {
            backgroundColor: 'var(--c-hover-on-light)',
          },
          '&:disabled': {
            backgroundColor: 'var(--c-background-light)',
            color: 'var(--c-text-placeholder)',
            '--icon-color': 'var(--c-text-placeholder)',
          },
          '& svg': {
            color: 'var(--icon-color, var(--c-primary))',
          },
        },
        containedInfo: {
          background: 'var(--c-background-light)',
          color: 'var(--c-secondary)',
          border: '1px solid #E8E8E8',
          '&:hover, &:active, &:focus-within': {
            backgroundColor: 'var(--c-hover-on-light)',
          },
          '&:disabled': {
            backgroundColor: 'var(--c-background-light)',
            color: 'var(--c-text-placeholder)',
          },
          '& svg': {
            color: 'inherit',
          },
        },
        containedSuccess: {
          background: 'var(--c-primary-light)',
          color: 'var(--c-primary)',
          '&:hover, &:active, &:focus-within:not(.nav-btn)': {
            backgroundColor: 'var(--c-hover-on-selected)',
          },
          '&:disabled': {
            backgroundColor: 'var(--c-primary-light)',
            color: 'var(--c-text-placeholder)',
            '--icon-color': 'var(--c-text-placeholder)',
          },
          '& svg': {
            color: 'var(--icon-color, var(--c-primary))',
          },
        },
        containedError: {
          backgroundColor: 'var(--c-error)',
          color: 'var(--c-text-contrast)',
          '&:hover, &:active, &:focus-within': {
            backgroundColor: 'var(--c-hover-on-error)',
          },
          '&:disabled': {
            backgroundColor: '#FFDDE6',
            color: '#FD9DB6',
          },
          '& svg': {
            color: 'inherit',
          },
        },
        textPrimary: {
          color: 'var(--c-primary)',
          '&:hover, &:active, &:focus-within': {
            backgroundColor: '#FAFCFF',
          },
          '&:disabled': {
            backgroundColor: 'none',
            color: 'var(--c-text-placeholder)',
          },
          '& svg': {
            color: 'inherit',
          },
        },
      },
      
      variants: [
        {
          props: {
            variant: 'contained',
          },
          style: {
            boxShadow: '0px 1px 6px 0px #30313d08',
          },
        },
        {
          props: {
            size: 'small',
            variant: 'contained',
          },
          style: {
            padding: '2px 16px',
            minHeight: 24,
          },
        },
      ],
    },

    MuiLink: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
          textDecoration: 'none',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: '0.813rem',
          border: 'none',
          textTransform: 'none',
          borderRadius: '4px',
          paddingTop: theme.spacing(0.1),
          paddingBottom: theme.spacing(0.1),
          paddingLeft: theme.spacing(0.5),
          paddingRight: theme.spacing(0.5),
        },
        '& + &': {
          borderLeft: '1px solid #ccc',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
          height: 34,
          alignItems: 'center',
          gap: 6,
          outlineStyle: 'solid',
          outlineWidth: '1px',
          outlineColor: theme.palette.tertiary.main,
          borderRadius: theme.shape.borderRadius,
          boxShadow: '0px 1px 6px 0px #30313d08',
          '& .MuiToggleButton-root.Mui-selected , .MuiToggleButton-root.Mui-selected:hover': {
            color: theme.palette.primary.main,
            backgroundColor: 'white ',
          },
        },
      },
    },
  };
};

export default Button;
